
import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, UserRole } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: UserRole;
  allowedRoles?: UserRole[];
  redirectTo?: string;
}

export default function ProtectedRoute({ 
  children, 
  requiredRole,
  allowedRoles = [], 
  redirectTo = '/auth' 
}: ProtectedRouteProps) {
  const { isAuthenticated, userRole, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-chrona-accent" />
          <p>Prüfe Berechtigungen...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login page
  if (!isAuthenticated) {
    return <Navigate 
      to={redirectTo} 
      state={{ 
        from: location.pathname,
        message: "Bitte melden Sie sich an, um auf diese Seite zuzugreifen." 
      }} 
      replace 
    />;
  }

  // Check if requiredRole is specified and user has it
  if (requiredRole && userRole !== requiredRole) {
    return <Navigate 
      to="/unauthorized" 
      state={{ 
        message: "Sie haben nicht die erforderlichen Berechtigungen, um auf diese Seite zuzugreifen.",
        requiredRole: requiredRole
      }} 
      replace 
    />;
  }

  // If allowedRoles are specified and user doesn't have any of them, redirect to unauthorized
  if (allowedRoles.length > 0 && (!userRole || !allowedRoles.includes(userRole))) {
    return <Navigate 
      to="/unauthorized" 
      state={{ 
        message: "Sie haben nicht die erforderlichen Berechtigungen, um auf diese Seite zuzugreifen.",
        requiredRoles: allowedRoles
      }} 
      replace 
    />;
  }

  return <>{children}</>;
}
